/** English strings: the source of truth. Other languages must provide every key (type-checked). */
export const en = {
  app: {
    name: "Smart Crop Protect",
    tagline: "Your field, forecast",
    offline: "You are offline. Showing saved data.",
    updated: (t: string) => `Updated ${t}`,
    staleData: "Could not refresh. Showing the last saved forecast.",
    loading: "Loading…",
    retry: "Try again",
    error: "Something went wrong.",
    readAloud: "Read aloud",
    voice: "Voice command",
    listening: "Listening… say weather, scan, money, plan or home",
    notHeard: "Could not hear you. Try again.",
    voiceUnsupported: "Voice commands are not supported in this browser.",
    speechUnsupported: "Read aloud is not supported on this device.",
    change: "Change",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    next: "Next",
    back: "Back",
    done: "Done",
    seeAll: "See all",
    viewTable: "View as table",
    viewChart: "View as chart",
    source: "Source",
    disclaimer:
      "General guidance only. For chemical choices and doses, follow the product label and your local agriculture officer or Krishi Vigyan Kendra.",
  },
  nav: {
    today: "Today",
    field: "Field",
    scan: "Scan",
    money: "Money",
    plan: "Plan",
    learn: "Learn",
    ai: "How the AI works",
    settings: "Settings",
  },
  greet: {
    morning: "Good morning",
    afternoon: "Good afternoon",
    evening: "Good evening",
  },
  units: {
    mm: "mm",
    kmh: "km/h",
    c: "°C",
    kgha: "kg/ha",
    tha: "t/ha",
    qacre: "quintal/acre",
    acres: "acres",
    perQuintal: "per quintal",
    perKg: "per kg",
    days: (n: number) => `${n} day${n === 1 ? "" : "s"}`,
    hours: (n: number) => `${n} h`,
  },
  level: {
    good: "Good",
    marginal: "Marginal",
    poor: "Poor",
    none: "None",
    low: "Low",
    moderate: "Moderate",
    high: "High",
    warning: "Warning",
    serious: "Serious",
    critical: "Critical",
  },
  stage: {
    presowing: "Not sown yet",
    initial: "Establishment",
    development: "Vegetative growth",
    mid: "Flowering & grain/fruit fill",
    late: "Ripening",
    harvest: "Ready to harvest",
    day: (n: number) => `Day ${n}`,
    sowsIn: (n: number) => `Sowing in ${n} days`,
    harvestIn: (n: number) => `Harvest in about ${n} days`,
    harvestOn: (d: string) => `Expected harvest ${d}`,
  },
  onboarding: {
    welcomeTitle: "Grow with confidence",
    welcomeBody:
      "Weather, water, pest risk and price forecasts for your own field. Built on government crop statistics and satellite weather. Works offline, and your data stays on your phone.",
    start: "Get started",
    languageTitle: "Choose your language",
    locationTitle: "Where is your field?",
    locationBody: "We use your location only to fetch weather and soil data. It stays on this device.",
    useGps: "Use my location",
    locating: "Finding your location…",
    orChoose: "Or choose your district",
    state: "State",
    district: "District",
    gpsFailed: "Could not get your location. Please choose your district.",
    outsideIndia: "This app covers India. Please choose a district.",
    cropTitle: "What are you growing?",
    season: "Season",
    sowingDate: "Sowing date",
    fieldTitle: "About your field",
    area: "Field size",
    irrigation: "Water source",
    ready: "All set",
    readyBody: "Your field dashboard is ready.",
  },
  irrigationMethod: {
    rainfed: "Rainfed only",
    flood: "Canal / flood",
    sprinkler: "Sprinkler",
    drip: "Drip",
  },
  today: {
    title: "Today",
    irrigation: "Irrigation",
    spraying: "Spraying",
    pestRisk: "Pest & disease risk",
    cropStage: "Crop stage",
    alerts: "Weather alerts",
    noAlerts: "No weather alerts this week.",
    week: "Next 7 days",
    outlook: "Harvest & price outlook",
    tasks: "Next tasks",
    tip: "Tip of the day",
    setLocation: "Set your field location to get your forecast.",
  },
  irrigate: {
    now: (mm: number) => `Irrigate today: about ${mm} mm`,
    by: (d: string) => `Irrigate by ${d}`,
    byAmount: (mm: number, l: string) => `About ${mm} mm (${l} litres per acre)`,
    hold: (mm: number, d: number) => `Hold: ${mm} mm rain expected in ${d} days`,
    ok: "No irrigation needed",
    okSub: (p: number) => `Root zone is ${100 - p}% full`,
    rainfedStress: (d: string) => `Dry stress likely from ${d}`,
    rainfedSub: "Mulch and save moisture; give protective irrigation if you can.",
    notSown: "Field not sown yet",
    harvest: "Crop is ready; stop irrigating",
    riceFlooded: "Keep 2–5 cm water in the field",
    riceAwd: (n: number) => `Re-flood about 5 cm: no good rain for ${n} days`,
    riceAwdSub: "Alternate wetting and drying saves about 25% water without yield loss.",
    riceDrain: "Drain the field: harvest is near",
    logged: "Irrigation logged",
    logToday: "I irrigated today",
  },
  spray: {
    goodNow: "Good time to spray now",
    window: (from: string, to: string) => `Best window: ${from}–${to}`,
    none: "No safe spray window in 2 days",
    noneSub: "Rain, wind or heat expected. Wait for the next forecast.",
    reason: {
      "rain-now": "Raining",
      "rain-soon": "Rain within 4 hours",
      "wind-high": "Too windy: drift",
      "wind-calm": "Very calm: inversion risk",
      hot: "Too hot",
      "dry-air": "Dry air: droplets evaporate",
      humid: "Very humid: slow drying",
      dark: "Dark",
    },
    planner: "Spray planner",
    plannerSub: "Next 48 hours, checked for rain, wind, heat and Delta-T.",
  },
  risk: {
    none: "Low risk this week",
    noneSub: "Weather is not favourable for major outbreaks.",
    title: "Pest & disease risk",
    sub: "Infection risk from hourly humidity, leaf wetness and temperature.",
    peak: (d: string) => `Highest on ${d}`,
    driver: {
      wetness: "Long leaf wetness",
      "warm-humid": "Warm and humid",
      "hot-dry": "Hot and dry",
      "cool-humid": "Cool and humid",
      "rain-wind": "Rain and wind",
      warm: "Warm weather",
    },
  },
  alert: {
    heat: (v: number) => `Heat wave: up to ${v}°C. Irrigate in the evening, avoid afternoon spraying.`,
    cold: (v: number) => `Cold nights down to ${v}°C. Light irrigation helps protect crops.`,
    frost: (v: number) => `Frost risk: ${v}°C. Irrigate lightly in the evening; cover nurseries.`,
    "heavy-rain": (v: number) => `Heavy rain: ${v} mm in a day. Open drains; delay fertiliser and spraying.`,
    wind: (v: number) => `Strong winds up to ${v} km/h. Stake tall crops; do not spray.`,
    storm: () => "Thunderstorms likely. Stay out of open fields during lightning.",
    hail: () => "Hail possible. Harvest ripe produce and secure stored grain.",
    "dry-spell": (v: number) => `No useful rain for ${v} days. Plan irrigation and mulch the soil.`,
  },
  field: {
    title: "Field",
    sub: "Weather, water and protection for your crop.",
    forecast: "10-day forecast",
    temperature: "Temperature",
    rainfall: "Rainfall",
    rainChance: (p: number) => `${p}% chance`,
    max: "Max",
    min: "Min",
    waterTitle: "Soil water",
    waterSub: "FAO-56 water balance of your root zone: past 14 days and the forecast.",
    depletion: "Water available to roots",
    raw: "Stress begins",
    taw: "Empty",
    irrigationTitle: "Irrigation advice",
    soilTitle: "Your soil",
    soilFrom: "From ISRIC SoilGrids (250 m)",
    soilManual: "Typical soil for the texture you chose",
    texture: "Texture",
    ph: "pH",
    awc: "Water holding",
    organicCarbon: "Organic carbon",
    phOk: "pH suits this crop",
    phLow: "Acidic for this crop: consider liming after a soil test",
    phHigh: "Alkaline for this crop: add organic matter or gypsum after a soil test",
    today: "Today",
    tomorrow: "Tomorrow",
    wind: "Wind",
    humidity: "Humidity",
  },
  scan: {
    title: "Crop doctor",
    sub: "Photograph a leaf or describe what you see. Analysis runs on your phone.",
    photoTab: "Photo",
    symptomsTab: "Symptoms",
    take: "Take or choose a photo",
    trySample: "No leaf at hand? Try a sample photo:",
    tips: "Fill the frame with one affected leaf, in daylight, in focus.",
    analyze: "Analyse photo",
    analyzing: "Analysing…",
    loadingModel: "Loading the AI model (first time only)…",
    modelUnavailable: "The leaf AI model is not installed yet. Showing the damage map only.",
    results: "Results",
    likely: "Most likely",
    possible: "Also possible",
    confidence: (p: number) => `${p}% confidence`,
    unsure: "Not sure. Try a closer, clearer photo of one leaf.",
    notLeaf: "This doesn't look like a leaf. Fill the frame with one affected leaf and try again.",
    cropNotCovered:
      "The photo model covers rice, maize, cotton, groundnut, potato and tomato. For this crop, the Symptoms tab is more reliable.",
    healthy: "Looks healthy",
    verify:
      "Field photos are harder than lab photos: check that the signs below match your leaf before you spray.",
    otherCrop: (c: string) => `This looks like ${c}, not your selected crop.`,
    severity: "Damaged leaf area",
    severityNote: "Estimated from leaf colour. Brown and yellow areas are marked.",
    whatToDo: "What to do",
    signs: "How to recognise it",
    favours: "Favoured by",
    cultural: "Field practices",
    biological: "Organic & biological",
    chemical: "Chemical (follow the label)",
    pickSymptoms: "What do you see?",
    findCauses: "Find likely causes",
    clear: "Clear",
    noMatch: "Pick at least one sign you see in the field.",
    matched: "Matches",
    weatherBoost: "Weather this week favours this",
    saved: "Saved to field log",
    saveLog: "Save to field log",
    log: "Field log",
    logEmpty: "Your saved scans appear here and stay available offline.",
    privacy: "Photos never leave your phone.",
  },
  symptom: {
    spots: "Spots on leaves",
    "eye-lesions": "Eye/spindle-shaped spots with grey centre",
    "concentric-rings": "Spots with rings like a target",
    "rust-pustules": "Rusty/orange powdery bumps",
    powder: "White powder on leaves",
    "water-soaked": "Dark water-soaked patches",
    "white-mould-under": "White/grey mould under leaves",
    "purple-patches": "Purple patches",
    "leaf-tip-dry": "Leaf tips/edges drying",
    yellowing: "Yellowing leaves",
    mosaic: "Yellow-green mosaic pattern",
    "leaf-curl": "Curled or crinkled leaves",
    silvering: "Silvery streaks",
    wilting: "Wilting even with moist soil",
    stunting: "Stunted growth",
    "rotting-stem": "Rotting stem or base",
    holes: "Holes or chewed leaves",
    frass: "Sawdust-like droppings in whorl",
    "bored-fruit": "Holes in pods, bolls or fruit",
    "dead-heart": "Central shoot dried (dead heart)",
    "sticky-insects": "Tiny insects, sticky leaves",
    webbing: "Fine webbing",
    "dry-cracked": "Scorched leaves, cracked dry soil",
    "standing-water": "Standing water, rotting roots",
    lodged: "Plants fallen flat",
    weeds: "Heavy weeds",
    "mouldy-grain": "Mouldy or damaged grain",
    trampled: "Trampled or eaten by animals",
  },
  kind: {
    all: "All",
    fungal: "Fungal",
    bacterial: "Bacterial",
    viral: "Virus",
    insect: "Insect",
    mite: "Mite",
    abiotic: "Weather & soil",
  },
  money: {
    title: "Harvest & money",
    sub: "District yield forecast, market outlook and support prices.",
    yieldTitle: "Expected yield",
    yieldSub: (d: string) => `Machine-learning forecast for ${d} district`,
    range: (a: string, b: string) => `Likely range ${a} – ${b}`,
    vsNormal: (p: number) =>
      p >= 0 ? `${p}% above your district's normal` : `${-p}% below your district's normal`,
    basedOn: (k: number, n: number) =>
      k === 0
        ? "Before the season: based on district history and normal weather"
        : `Uses ${k} of ${n} months of this season's weather`,
    rainSoFar: (p: number) => `Season rainfall so far: ${p}% of normal`,
    scenarioTitle: "If the rest of the season is…",
    dry: "Dry",
    normal: "Normal",
    wet: "Wet",
    history: "District yields",
    forecastMark: "Forecast",
    noYieldData: "No district statistics for this crop and season, so no yield forecast.",
    tomatoNote: "Government district statistics do not cover this crop yet.",
    incomeTitle: "Income estimate",
    incomeSub: "Your field size × forecast yield × price.",
    production: "Expected production",
    priceUsed: "Price used",
    grossIncome: "Gross income",
    mspLabel: (s: string) => `MSP ${s}`,
    marketLabel: "Market price (forecast)",
    pricesTitle: "Market price outlook",
    pricesSub: (z: string) => `Retail prices in the ${z} zone, with an 80% forecast range`,
    latest: (m: string) => `Latest reported (${m})`,
    expected: (m: string) => `Expected in ${m}`,
    noPrice: "No market price series for this crop. Check live mandi rates below.",
    retailNote: "Retail prices show the direction of the market; mandi prices you receive are lower.",
    mandiTitle: "Today's mandi prices",
    mandiSub: (d: string) => `Agmarknet wholesale prices, updated ${d}`,
    modal: "Modal price",
    mspTitle: "Minimum Support Price",
    mspNone: "No MSP is announced for this crop.",
    helpTitle: "Sell, insure and get help",
    agmarknet: "Live mandi prices (Agmarknet)",
    enam: "Sell online (e-NAM)",
    pmfby: "Crop insurance (PMFBY)",
    kvk: "Find your Krishi Vigyan Kendra",
    kcc: "Kisan Call Centre 1800-180-1551",
    zone: { north: "North", south: "South", east: "East", west: "West", northeast: "North-East" },
  },
  plan: {
    title: "Season plan",
    sub: "Your crop timeline, tasks and next-season choices.",
    timeline: "Crop timeline",
    sowing: "Sowing",
    harvest: "Harvest",
    tasksTitle: "Checklist",
    addTask: "Add your own task",
    addPlaceholder: "For example: spray neem on the north plot",
    add: "Add",
    advisorTitle: "What to grow next season",
    advisorSub: (s: string, d: string) => `Crops ranked by expected income per acre for ${s} in ${d}`,
    expectedYield: "Expected yield",
    perAcre: "Income per acre",
    risk: "Downside (bad year)",
    noAdvisor: "Not enough district data to compare crops.",
    stageTasks: {
      initial: [
        "Fill gaps where seedlings failed",
        "First weeding 20–25 days after sowing",
        "Check for seedling pests and damping off",
      ],
      development: [
        "Top-dress nitrogen as per soil test",
        "Second weeding or inter-row hoeing",
        "Scout twice a week for pests",
      ],
      mid: [
        "Do not let the crop dry out at flowering",
        "Protect flowers and pods/grains from pests",
        "Install pheromone traps and bird perches",
      ],
      late: [
        "Stop irrigation 10–15 days before harvest",
        "Arrange harvest labour or machine",
        "Clean and prepare storage; check bags",
      ],
      presowing: [
        "Get your soil tested",
        "Buy certified seed of a recommended variety",
        "Prepare drainage channels",
      ],
      harvest: [
        "Dry produce well before storing",
        "Check market prices before selling",
        "Remove crop residue safely; do not burn",
      ],
    },
  },
  learn: {
    title: "Learn",
    sub: "Pests, diseases and other causes of crop loss, and how to manage them.",
    search: "Search pests and diseases",
    forCrop: (c: string) => `Showing threats for ${c}`,
    allCrops: "All crops",
  },
  settings: {
    title: "Settings",
    profile: "Your field",
    location: "Location",
    crop: "Crop",
    language: "Language",
    theme: "Appearance",
    themeSystem: "System",
    themeLight: "Light",
    themeDark: "Dark",
    textSize: "Text size",
    soil: "Soil texture",
    soilAuto: "Automatic (SoilGrids)",
    soilSandy: "Sandy",
    soilLoamy: "Loamy",
    soilClay: "Clay",
    soilBlack: "Black cotton soil",
    privacy: "Privacy",
    privacyBody:
      "Everything stays on this device. Weather, soil and satellite data are fetched from public services using your field's coordinates only. There are no accounts and no tracking.",
    reset: "Reset all data",
    resetConfirm: "Delete your field profile, tasks and saved scans from this device?",
    about: "About",
    version: (v: string) => `Version ${v}`,
  },
  tips: [
    "Scout early in the morning, when pests are easiest to see.",
    "Water at the base of plants to keep leaves dry and reduce disease.",
    "Rotate crops each season to break pest and disease cycles.",
    "Keep tools clean so diseases do not spread between plots.",
    "Check the underside of leaves, where many pests hide.",
    "Dry harvested grain fully before storing it.",
    "Keep field edges free of weeds that shelter pests.",
  ],
  months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

type Widen<T> = T extends (...args: infer A) => string
  ? (...args: A) => string
  : T extends string
    ? string
    : T extends readonly string[]
      ? string[]
      : { [K in keyof T]: Widen<T[K]> };

export type Messages = Widen<typeof en>;
